import { SupportedSearchKeywords } from '../../providers/atweb-query-expression-parser.service';
import { ValueAdapter, ChipExpression } from '@nrwl/query-emitter';

export interface AliasedExpression {
  expression: string;
  aliases: {
    // nested {} object in form: {'alias1': 'field_prefix_x', 'alias2': 'field_prefix_n', ...}
    source: any;
    // nested {} object in form: {'alias1': 'field_prefix_x', 'alias2': 'field_prefix_n', ...}
    destination: any;
  };
}

export interface KeywordProperties {
  // fieldPrefix must be specified if field can be aliased
  fieldPrefix?: string;
  fieldName: string;
  placement: 'src' | 'dst' | 'no-direction' | 'no-alias';
  // alias is mandatory when placement src or dst
  alias?: string;
}

export const outgoingValueAdapterConfig = {
  direction: 'outgoing',
  useAliases: true
};

export const incomingValueAdapterConfig = {
  direction: 'incoming',
  useAliases: true
};
// this is very special case, when ip2 must be replaced with serverip..
export const incomingServerValueAdapterConfig = {
  direction: 'incoming-to-serverip',
  useAliases: false
};

export const outgoingClientServerValueAdapterConfig = {
  direction: 'outgoing',
  useAliases: false
};

export const incomingClientServerValueAdapterConfig = {
  direction: 'incoming',
  useAliases: false
};

export abstract class CombinedFlowAdapter extends ValueAdapter {
  keywordsToIncoming: Map<string, KeywordProperties> = new Map();
  keywordsToIncomingServer: Map<string, KeywordProperties> = new Map();
  keywordsToOutgoing: Map<string, KeywordProperties> = new Map();
  keywordsNoDirection: Map<string, KeywordProperties> = new Map();
  // eg {"direction":"incoming", "useAliases": true}
  // possible values for 'direction': incoming, outgoing, both
  config;

  constructor(config) {
    super();
    this.config = config;
    this.populateIncoming();
    this.populateOutgoing();
    this.populateNoDirection();
  }

  // notice we don't know bindings for location and server keywords!
  populateIncoming() {
    this.keywordsToIncoming.set(SupportedSearchKeywords.App, { placement: Placement.noAlias, fieldName: 'app' });
    this.keywordsToIncoming.set(SupportedSearchKeywords.Server, { placement: Placement.noAlias, fieldName: 'serverip' });
    this.keywordsToIncoming.set(SupportedSearchKeywords.Site, {
      placement: Placement.dst,
      fieldPrefix: 'site',
      alias: 'site_dst',
      fieldName: 'site2'
    });
    this.keywordsToIncoming.set(SupportedSearchKeywords.User, {
      placement: Placement.dst,
      fieldPrefix: 'user',
      alias: 'user_dst',
      fieldName: 'user2'
    });
    this.keywordsToIncoming.set('ip', { placement: Placement.dst, fieldPrefix: 'ip', alias: 'ip_dst', fieldName: 'ip2' });

    this.keywordsToIncomingServer.set(SupportedSearchKeywords.App, { placement: Placement.noAlias, fieldName: 'app' });
    this.keywordsToIncomingServer.set(SupportedSearchKeywords.Server, { placement: Placement.noAlias, fieldName: 'serverip' });
    this.keywordsToIncomingServer.set(SupportedSearchKeywords.Site, {
      placement: Placement.dst,
      fieldPrefix: 'site',
      alias: 'site_dst',
      fieldName: 'site2'
    });
    this.keywordsToIncomingServer.set(SupportedSearchKeywords.User, {
      placement: Placement.dst,
      fieldPrefix: 'user',
      alias: 'user_dst',
      fieldName: 'user2'
    });
    this.keywordsToIncomingServer.set('ip', { placement: Placement.dst, fieldPrefix: 'ip', alias: 'serverip', fieldName: 'serverip' });
  }

  populateOutgoing() {
    this.keywordsToOutgoing.set(SupportedSearchKeywords.App, { placement: Placement.noAlias, fieldName: 'app' });
    this.keywordsToOutgoing.set(SupportedSearchKeywords.Server, { placement: Placement.noAlias, fieldName: 'serverip' });
    this.keywordsToOutgoing.set(SupportedSearchKeywords.Site, {
      placement: Placement.src,
      fieldPrefix: 'site',
      alias: 'site_src',
      fieldName: 'site1'
    });
    this.keywordsToOutgoing.set(SupportedSearchKeywords.User, {
      placement: Placement.src,
      fieldPrefix: 'user',
      alias: 'user_src',
      fieldName: 'user1'
    });
    this.keywordsToOutgoing.set('ip', { placement: Placement.src, fieldPrefix: 'ip', alias: 'ip_src', fieldName: 'ip1' });
  }

  populateNoDirection() {
    this.keywordsNoDirection.set(SupportedSearchKeywords.App, { placement: Placement.noAlias, fieldName: 'app' });
    this.keywordsNoDirection.set(SupportedSearchKeywords.Server, { placement: Placement.noAlias, fieldName: 'serverip' });
    this.keywordsNoDirection.set(SupportedSearchKeywords.Site, {
      placement: Placement.noDirectional,
      fieldPrefix: 'site',
      alias: 'site',
      fieldName: 'site1'
    });
    this.keywordsNoDirection.set(SupportedSearchKeywords.User, {
      placement: Placement.noDirectional,
      fieldPrefix: 'user',
      alias: 'user',
      fieldName: 'user1'
    });
    this.keywordsNoDirection.set('ip', { placement: Placement.noDirectional, fieldPrefix: 'ip', alias: 'ip', fieldName: 'ip1' });
  }

  useAliases() {
    return this.config && this.config.useAliases ? this.config.useAliases : false;
  }

  // value is expected to be an array of ChipExpression
  processValue(value) {
    let mappingTable;
    if (this.config && this.config.direction) {
      switch (this.config.direction) {
        case 'incoming':
          {
            mappingTable = this.keywordsToIncoming;
          }
          break;
        case 'incoming-to-serverip':
          {
            mappingTable = this.keywordsToIncomingServer;
          }
          break;
        case 'outgoing':
          {
            mappingTable = this.keywordsToOutgoing;
          }
          break;
        case 'both':
          {
            mappingTable = this.keywordsNoDirection;
          }
          break;
        default:
          throw new Error('unknown direction!');
      }
    }
    return this.build(value as ChipExpression[], mappingTable);
  }

  abstract build(value: ChipExpression[], mappingTable: Map<string, KeywordProperties>): AliasedExpression;
}

enum Placement {
  noAlias = 'no-alias',
  dst = 'dst',
  src = 'src',
  noDirectional = 'no-direction'
}
